import React, {useState} from 'react';
import {classNames} from '@utils/class-names';
import {SpecialistSearchResultDto} from "../../core/models/interfaces/specialists-search-result-dto.interface";
import {ReactComponent as DeleteIcon} from "@assets/icons/delete.svg";
import {UniversalClickHandler} from "@common/models/types/universal-click-handler.type";
import {ModalIds} from '@common/models/enums/modal-ids.enum';
import useModalWithData from "@common/hooks/use-modal-with-data";
import {formatPhoneNumber} from "../utils/format-phone-number";
import SelectedCheckmark from "../../articles/components/ui/selected-checkmark";
import SpecialistProfileImage from "./specialist-profile-image";
import {SpecialistDeleteModalData} from "./specialist-delete-confirm-modal";

interface SpecialistCardProps {
    specialist: SpecialistSearchResultDto;
    className?: string;
    onClick?: UniversalClickHandler<void, SpecialistSearchResultDto>;
    showActions?: boolean;
    isSelected?: boolean;
    onSuccessDelete?: UniversalClickHandler<void, string>;
}

const SpecialistCard: React.FC<SpecialistCardProps> = (props: SpecialistCardProps) => {
    const {
        specialist,
        className = '',
        showActions = true,
        onClick,
        isSelected = false,
        onSuccessDelete,
    } = props;
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const {showModal: showSpecialistFormModal} = useModalWithData(ModalIds.SPECIALIST_FORM);
    const {showModal: showDeleteConfirmModal} = useModalWithData<SpecialistDeleteModalData>(ModalIds.SPECIALIST_DELETE_CONFIRM);

    const clickEditSpecialist = () => {
        showSpecialistFormModal({
            specialistId: specialist.id,
        });
    };

    const onClickHandler = () => onClick ? onClick(specialist) : clickEditSpecialist()

    const handleDelete = async (e: any) => {
        e.stopPropagation();
        showDeleteConfirmModal({
            id: specialist.id,
            fullName: specialist.fullName,
            onSuccess: () => onSuccessDelete && onSuccessDelete(specialist.id)
        });
    };

    return (
        <div
            className={classNames(
                'h-12 group rounded-2xl px-2 cursor-pointer transition-all duration-200',
                isSelected && "bg-success/10",
                !isSelected && !showActions && "hover:bg-input-background/5",
                !isPopoverOpen && !isSelected && "hover:bg-input-background/5",
                isPopoverOpen && 'bg-input-background/5',
                "flex justify-between items-center",
                className
            )}
            onClick={onClickHandler}
        >
            <div className="flex gap-4 items-center">
                <SpecialistProfileImage
                    specialistId={specialist.id}
                    specialistName={specialist.fullName}
                />

                <div className="flex-1 flex flex-col gap-1">
                    <h3 className="font-semibold text-base leading-[20px]">{specialist.fullName}</h3>
                    <div className="text-sm text-on-surface/80 leading-[18px]">
                        <span>{specialist.specialization}</span>
                        <span className="mx-2 text-[8px] bottom-[1px] relative">•</span>
                        <span>{formatPhoneNumber(specialist.phoneNumber)}</span>
                    </div>
                </div>
            </div>

            {isSelected && (
                <div className={classNames("bg-success", "rounded-full")}>
                    <SelectedCheckmark/>
                </div>
            )}

            {showActions && (
                <div className={classNames(
                    "transition-opacity duration-100 flex",
                    isPopoverOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}>
                    <button
                        onClick={handleDelete}
                        className={classNames(
                            'w-10 h-10 p-2 flex items-center justify-center',
                            'hover:bg-input-background/5 rounded-full',
                        )}
                    >
                        <DeleteIcon className="w-5 h-5"/>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SpecialistCard;
